/**
 * MagazineLayout - Magazine-style layout using CSS Multi-column Layout
 *
 * Displays photos in a responsive CSS columns layout with automatic vertical flow.
 * Items flow down one column before moving to the next (newspaper-style).
 * Uses container width and zoom to calculate optimal column count dynamically (1-12 columns).
 *
 * @module PhotoCollection2/layouts/MagazineLayout
 */

import { useState, useEffect, useRef } from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { PhotoItem2 } from '../../PhotoItem2';
import { PhotoContextMenu } from '../../PhotoContextMenu';
import { cn } from '@/lib/utils';
import { Text } from '../../atoms/Text';
import { useAuth } from '@/services/auth';
import type { PhotoCollection2LayoutProps, ReorderContext } from '../PhotoCollection2.types';
import type { PhotoItem2ViewModel } from '../../PhotoItem2/PhotoItem2.types';

/**
 * Base target width for calculating column count at 100% zoom
 * Aligned with GridLayout for consistent behavior across layouts
 */
const BASE_TARGET_WIDTH = 300;

/**
 * Props for SortableMagazineItem wrapper
 */
interface SortableMagazineItemProps {
  photo: PhotoItem2ViewModel;
  itemTemplate: PhotoCollection2LayoutProps['itemTemplate'];
  selectionMode: PhotoCollection2LayoutProps['selectionMode'];
  isSelected: boolean;
  zoom: number;
  onItemClick?: (photoId: string, event?: React.MouseEvent) => void;
  reorderContext?: ReorderContext;
  hasSelection: boolean;
  /** Current user's ID for ownership check (Feature 081: photo-versioning) */
  currentUserId?: string;
}

/**
 * Wrapper component that makes the entire photo item draggable in magazine layout
 */
function SortableMagazineItem({
  photo,
  itemTemplate,
  selectionMode,
  isSelected,
  zoom,
  onItemClick,
  reorderContext,
  hasSelection,
  currentUserId,
}: SortableMagazineItemProps) {
  const isDragEnabled = reorderContext?.enabled && !hasSelection;

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: photo.photoData.id,
    disabled: !isDragEnabled,
  });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 50 : undefined,
    cursor: isDragEnabled ? (isDragging ? 'grabbing' : 'grab') : undefined,
  };

  const dragProps = isDragEnabled
    ? {
        ...attributes,
        ...listeners,
        'aria-roledescription': 'sortable',
        'aria-describedby': 'dnd-instructions',
      }
    : {};

  // Feature 081: photo-versioning - Check if current user owns this photo
  const isOwner = currentUserId ? photo.photoData.userId === currentUserId : false;

  return (
    <div
      ref={setNodeRef}
      {...dragProps}
      className="break-inside-avoid mx-4 mb-4"
      style={style}
    >
      <PhotoContextMenu
        photoId={photo.photoData.id}
        isOwner={isOwner}
      >
        <PhotoItem2
          data={photo}
          template={itemTemplate}
          isSelected={selectionMode !== 'none' && isSelected}
          selectable={selectionMode !== 'none'}
          zoom={zoom}
          onClick={(e?: React.MouseEvent) => onItemClick?.(photo.photoData.id, e)}
        />
      </PhotoContextMenu>
    </div>
  );
}

/**
 * Maximum number of columns supported (matches Tailwind safelist range)
 * Prevents exceeding lg:columns-1 through lg:columns-12 safelist entries
 */
const MAX_COLUMNS = 12;

/**
 * MagazineLayout component
 *
 * Renders photos in a responsive CSS Multi-column Layout with automatic item distribution.
 * Items flow vertically down one column before moving to the next (newspaper-style).
 *
 * Responsive breakpoints:
 * - Mobile (default): 1 column
 * - Tablet (md, ≥768px): 2 columns
 * - Desktop (lg, ≥1024px): Dynamic 1-12 columns based on container width and zoom
 *
 * Column calculation:
 * - zoom 0.25 (25%): Maximum density (clamped to 12 columns)
 * - zoom 1.0 (100%): ~4 columns for 1200px container
 * - zoom 5.0 (500%): Minimum density (usually 1 column)
 *
 * Features:
 * - Automatically recalculates columns when container resizes
 * - `break-inside-avoid` prevents photos from splitting across columns
 * - Browser-optimized column balancing
 *
 * @example
 * ```typescript
 * <MagazineLayout
 *   items={photos}
 *   itemTemplate="photo-only"
 *   selectionMode="multiple"
 *   selectedIds={['photo-1']}
 *   zoom={1.0}
 *   onItemClick={handleClick}
 * />
 * ```
 */
export function MagazineLayout({
  items,
  itemTemplate,
  selectionMode,
  selectedIds,
  zoom = 1.0,
  onItemClick,
  reorderContext,
  className,
}: PhotoCollection2LayoutProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState<number>(0);

  // Feature 081: photo-versioning - Get current user for ownership check
  const { user } = useAuth();
  const currentUserId = user?.id;

  // Track container width with ResizeObserver
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    // Set initial width
    setContainerWidth(container.offsetWidth);

    // Create ResizeObserver to track width changes
    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setContainerWidth(entry.contentRect.width);
      }
    });

    resizeObserver.observe(container);

    return () => {
      // Explicitly unobserve the container to prevent memory leaks
      resizeObserver.unobserve(container);
      resizeObserver.disconnect();
    };
  }, []);

  // Calculate base columns based on container width and zoom
  // Clamp to 1-12 range to match Tailwind safelist (lg:columns-1 through lg:columns-12)
  let baseColumns = containerWidth > 0 ? Math.round((containerWidth/zoom)/BASE_TARGET_WIDTH) : 4;
  baseColumns = Math.max(1, Math.min(MAX_COLUMNS, baseColumns));

  // Map baseColumns to explicit Tailwind class (safer than template literals)
  const COLUMN_CLASSES = [
    'lg:columns-1', 'lg:columns-2', 'lg:columns-3', 'lg:columns-4',
    'lg:columns-5', 'lg:columns-6', 'lg:columns-7', 'lg:columns-8',
    'lg:columns-9', 'lg:columns-10', 'lg:columns-11', 'lg:columns-12'
  ] as const;
  const columnClass = COLUMN_CLASSES[baseColumns - 1] || 'lg:columns-4';

  // Check if any items are selected (for disabling drag during selection)
  const hasSelection = selectedIds.length > 0;

  // Empty state
  if (items.length === 0) {
    return (
      <div className={cn('flex items-center justify-center h-64 text-center', className)}>
        <Text variant="muted">No photos in this collection</Text>
      </div>
    );
  }

  // Note: SortableContext is provided by PhotoCollection2, not here
  // This prevents double-wrapping which could cause drag-and-drop issues
  return (
    <div
      ref={containerRef}
      className={cn('columns-1 md:columns-2', columnClass, 'gap-0', className)}
    >
      {items.map((photo) => {
        // Feature 081: photo-versioning - Check if current user owns this photo
        const isOwner = currentUserId ? photo.photoData.userId === currentUserId : false;

        return reorderContext?.enabled ? (
          <SortableMagazineItem
            key={photo.photoData.id}
            photo={photo}
            itemTemplate={itemTemplate}
            selectionMode={selectionMode}
            isSelected={selectedIds.includes(photo.photoData.id)}
            zoom={zoom}
            onItemClick={onItemClick}
            reorderContext={reorderContext}
            hasSelection={hasSelection}
            currentUserId={currentUserId}
          />
        ) : (
          <div
            key={photo.photoData.id}
            className="break-inside-avoid mx-4 mb-4"
          >
            <PhotoContextMenu
              photoId={photo.photoData.id}
              isOwner={isOwner}
            >
              <PhotoItem2
                data={photo}
                template={itemTemplate}
                isSelected={selectionMode !== 'none' && selectedIds.includes(photo.photoData.id)}
                selectable={selectionMode !== 'none'}
                zoom={zoom}
                onClick={(e?: React.MouseEvent) => onItemClick?.(photo.photoData.id, e)}
              />
            </PhotoContextMenu>
          </div>
        );
      })}
    </div>
  );
}
